import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ClipboardCopy, ExternalLink, FileText, Loader2, Send } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ActionButtonRow } from '@/components/bookings/dialogKit'
import {
  grantMedicalClearance,
  listIntakeForms,
  listIntakeSubmissions,
  sendIntakeForm,
  type IntakeForm,
  type IntakeSubmission,
} from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { fmtDateTime } from '@/lib/format'

// ─── Intake, from the booking sheet ────────────────────────────────────────────
//
// Requested 2026-08-17: "If the intake form is outstanding, show: Open and
// complete now · Send to client · Copy intake form link. If medical clearance
// is required, show a clear warning before check-in."
//
// The dialog fetches its own state when opened (this customer's submissions +
// the default form), so it works from any surface the sheet renders on — it
// never depends on the /schedule day-sheet enrichment.
//
// "Open and complete now" opens the SAME public page the customer would get,
// via a link minted with deliver:false — the operator hands the device over.
// One open submission per (customer, form) is a DB index, so every path here
// converges on the same link rather than minting duplicates.

export interface IntakeDialogProps {
  customer: { id: string; name: string } | null
  bookingId?: string
  /** The booked service, so a manual send resolves the SAME form the auto-send
   * would (the service's own form, else the default) rather than always the
   * default. Absent falls back to the default form. */
  serviceId?: string | null
  onOpenChange: (open: boolean) => void
  /** Called after anything changed (sent, cleared) so the day refreshes. */
  onChanged?: () => void
  /**
   * Open the submission itself — the only place the ANSWERS are readable, which
   * is what "review their answers" above asks the operator to do.
   *
   * Passed in rather than reached for with `useNavigate`, because this dialog
   * draws inside the calendar sheet and its own tests mount it bare: a router
   * dependency here would make the component unmountable without one, for a
   * single link the caller already knows how to follow.
   */
  onViewAnswers?: (submissionId: string) => void
}

export function IntakeDialog({
  customer, bookingId, serviceId, onOpenChange, onChanged, onViewAnswers,
}: IntakeDialogProps) {
  const open = customer !== null

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [defaultForm, setDefaultForm] = useState<IntakeForm | null>(null)
  const [latest, setLatest] = useState<IntakeSubmission | null>(null)
  const [busy, setBusy] = useState<'send' | 'copy' | 'open' | 'clear' | null>(null)

  useEffect(() => {
    if (!open || !customer) return
    let live = true
    setLoading(true)
    setLoadError(null)
    setDefaultForm(null)
    setLatest(null)
    setBusy(null)
    Promise.all([
      listIntakeForms({ limit: 100 }),
      listIntakeSubmissions({ customerId: customer.id, limit: 1 }),
    ])
      .then(([forms, submissions]) => {
        if (!live) return
        setDefaultForm(forms.items.find((f) => f.is_default && f.status === 'active') ?? null)
        setLatest(submissions.items[0] ?? null)
      })
      .catch((e) => { if (live) setLoadError(readApiError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, customer?.id])

  async function send() {
    if (!customer || busy) return
    setBusy('send')
    try {
      const result = await sendIntakeForm({
        customer_id: customer.id,
        ...(serviceId ? { service_id: serviceId } : {}),
        ...(bookingId ? { booking_id: bookingId } : {}),
      })
      setLatest(result.submission)
      if (result.sent_via.length > 0) {
        toast.success(`Intake form sent to ${customer.name}`, {
          description: `Went by ${result.sent_via.join(' and ')}.`,
        })
      } else {
        const reasons = Object.values(result.delivery_errors).join(' · ')
        toast.error('The link could not be delivered', { description: reasons || undefined })
      }
      onChanged?.()
    } catch (e) {
      toast.error("Couldn't send the form", { description: readApiError(e) })
    } finally {
      setBusy(null)
    }
  }

  // Mint/fetch the link WITHOUT messaging anyone.
  async function mintLink(): Promise<string | null> {
    if (!customer) return null
    const result = await sendIntakeForm({
      customer_id: customer.id,
      deliver: false,
      ...(serviceId ? { service_id: serviceId } : {}),
      ...(bookingId ? { booking_id: bookingId } : {}),
    })
    setLatest(result.submission)
    return result.link
  }

  async function copyLink() {
    if (busy) return
    setBusy('copy')
    try {
      const link = await mintLink()
      if (link) {
        await navigator.clipboard.writeText(link)
        toast.success('Link copied', { description: 'Paste it anywhere — it only opens this form.' })
        onChanged?.()
      }
    } catch (e) {
      toast.error("Couldn't copy the link", { description: readApiError(e) })
    } finally {
      setBusy(null)
    }
  }

  async function openNow() {
    if (busy) return
    setBusy('open')
    try {
      const link = await mintLink()
      if (link) {
        window.open(link, '_blank', 'noopener')
        onChanged?.()
      }
    } catch (e) {
      toast.error("Couldn't open the form", { description: readApiError(e) })
    } finally {
      setBusy(null)
    }
  }

  async function grant() {
    if (!latest || busy) return
    setBusy('clear')
    try {
      const updated = await grantMedicalClearance(latest.id)
      setLatest(updated)
      toast.success('Medical clearance granted')
      onChanged?.()
    } catch (e) {
      toast.error("Couldn't grant clearance", { description: readApiError(e) })
    } finally {
      setBusy(null)
    }
  }

  const completed = latest?.status === 'completed'
  const awaiting = latest?.status === 'sent' || latest?.status === 'not_sent'
  const clearancePending = latest?.clearance_status === 'pending'
  const noForm = !loading && !defaultForm

  const statusLine = loading
    ? null
    : completed
      ? `Completed ${latest?.completed_at ? fmtDateTime(latest.completed_at) : ''}${latest?.completed_by && latest.completed_by !== 'customer' ? ` by ${latest.completed_by}` : ''}`
      : awaiting
        ? `Sent${latest?.sent_at ? ` ${fmtDateTime(latest.sent_at)}` : ''} — not answered yet`
        : 'Not sent yet'

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Intake form</DialogTitle>
          <DialogDescription>{customer?.name ?? ''}</DialogDescription>
        </DialogHeader>

        {customer ? (
          <div className="flex flex-col gap-3.5">
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Checking their intake…
              </div>
            ) : loadError ? (
              <p className="text-[12.5px] text-destructive">{loadError}</p>
            ) : noForm && !latest ? (
              <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12.5px] text-muted-foreground">
                No active default intake form yet. Build one under Comms → Intake forms and set
                it as the default, then send it from here.
              </p>
            ) : (
              <>
                <p className="text-[12.5px] text-muted-foreground">{statusLine}</p>

                {clearancePending ? (
                  <div className="rounded-lg border border-amber-500/60 bg-amber-500/10 px-3 py-2.5">
                    <p className="text-[12.5px] font-semibold text-foreground">
                      Medical clearance required — not yet granted.
                    </p>
                    <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                      An intake answer flagged this client. Review their answers, then grant
                      clearance once you are satisfied.
                    </p>
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void grant()}
                      className="mt-2 rounded-lg border border-amber-500/60 bg-card px-3 py-1.5 text-[12.5px] font-semibold text-foreground hover:bg-amber-500/20 disabled:opacity-50"
                    >
                      {busy === 'clear' ? 'Granting…' : 'Grant medical clearance'}
                    </button>
                  </div>
                ) : null}

                {latest && latest.status !== 'not_sent' && onViewAnswers ? (
                  <ActionButtonRow
                    icon={<FileText className="size-4" aria-hidden />}
                    label="View answers"
                    hint={completed ? 'What they filled in' : 'The form as sent — no answers yet'}
                    disabled={busy !== null}
                    onClick={() => {
                      onOpenChange(false)
                      onViewAnswers(latest.id)
                    }}
                  />
                ) : null}

                {latest?.clearance_status === 'granted' ? (
                  <p className="text-[12px] text-muted-foreground">
                    Medical clearance granted
                    {latest.clearance_granted_by ? ` by ${latest.clearance_granted_by}` : ''}
                    {latest.clearance_granted_at ? ` · ${fmtDateTime(latest.clearance_granted_at)}` : ''}.
                  </p>
                ) : null}

                {!completed && defaultForm ? (
                  <div className="flex flex-col gap-1.5 border-t border-border pt-3">
                    <ActionButtonRow
                      icon={<ExternalLink className="size-4" aria-hidden />}
                      label="Open and complete now"
                      hint="Opens the form here — hand them the screen"
                      disabled={busy !== null}
                      busy={busy === 'open'}
                      onClick={() => void openNow()}
                    />
                    <ActionButtonRow
                      icon={<Send className="size-4" aria-hidden />}
                      label={awaiting ? 'Send the link again' : 'Send to client'}
                      hint="Emails and texts them the link"
                      disabled={busy !== null}
                      busy={busy === 'send'}
                      onClick={() => void send()}
                    />
                    <ActionButtonRow
                      icon={<ClipboardCopy className="size-4" aria-hidden />}
                      label="Copy intake form link"
                      hint="Nothing is sent — paste it wherever you like"
                      disabled={busy !== null}
                      busy={busy === 'copy'}
                      onClick={() => void copyLink()}
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
